import React, { useEffect, useState } from 'react';
import { alpha, Box, Stack } from '@mui/material';
import { useSelector } from 'react-redux';
import { useMiNavigation } from 'hooks/useNavigation';
import { conciergeAPI, ITileInfoData } from 'api/concierge';
import DatabaseIcon from 'components/common/icon/DatabaseIcon';
import { TileInfoIcon } from 'components/tile-info-icon';
import { ISourceReports, SourceLeg } from 'app/concierge/types';
import { HtmlTooltip } from 'components/html-tooltip';
import useBundleTranslation from 'i18n';
import ReportWhyTooltip from './ReportWhyTooltip';
import { useConfigConstantValue } from 'hooks/useConfigConstant';
import { isSameSiteHttpUrl } from 'tools/safeHtml';

interface IProps {
    data: ISourceReports;
}

/**
 * The reports behind the answer (Marius, 2026-09-08): the reports of the model that answered
 * which this user may open, as compact chips right under the answer text, each with its
 * source-type icon and opening the report. The chips draw at once from the streamed block; the
 * tile lookup only upgrades the icon and attaches the preview, so a failed lookup never loses
 * a chip. Nothing when there is no report to name.
 */
/** The leg chip's tooltip (D58 pattern): the leg's model, measure and covered window, one line each. */
function LegTooltipBody({ leg, t }: { leg: SourceLeg; t: (key: string, options?: any) => string }) {
    const coverage =
        leg.coverage === 'none'
            ? t('message.leg_window_uncovered', { defaultValue: 'no data in this window' })
            : leg.coverage === 'partial' && leg.horizon_end
              ? t('message.leg_window_partial', { horizon: leg.horizon_end, defaultValue: 'data through {{horizon}}' })
              : '';
    return (
        <Box sx={{ p: '8px 10px', fontSize: '12px', lineHeight: '18px' }} data-test={`source-leg-tooltip-${leg.leg}`}>
            <Box>
                <strong>{t('message.leg_model', { defaultValue: 'Model' })}:</strong> {leg.model}
            </Box>
            <Box>
                <strong>{t('message.leg_measure', { defaultValue: 'Measure' })}:</strong> {leg.measure}
            </Box>
            {leg.window ? (
                <Box>
                    <strong>{t('message.leg_window', { defaultValue: 'Window' })}:</strong> {leg.window}
                    {coverage ? ` (${coverage})` : ''}
                </Box>
            ) : null}
        </Box>
    );
}

export default function SourceReports({ data }: IProps) {
    const navigate = useMiNavigation();
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;
    const roothome = useConfigConstantValue('ROOTHOME') as string | undefined;
    // A leg chip stands on its name alone (a leg with no openable report has no url), and so does
    // the row that names the model itself when it has no report mapping at all (reason, D4510).
    const reports = (data.reports ?? []).filter((r) => r && r.name && (r.url || r.leg || r.reason));
    const [tiles, setTiles] = useState<Record<number, ITileInfoData>>({});

    useEffect(() => {
        if (!reports.length) return;
        let cancelled = false;
        const withTiles = reports.filter((r) => Number(r.element_id) > 0);
        if (!withTiles.length) return;
        conciergeAPI
            .getTilesInfo(withTiles.map((r) => `${r.element_id}_0`))
            .then((rows) => {
                if (cancelled) return;
                const byId: Record<number, ITileInfoData> = {};
                (rows ?? []).forEach((row) => {
                    byId[Number(row.element_id)] = row;
                });
                setTiles(byId);
            })
            .catch(() => {
                /* the chips stand without the tile: name and link come from the block */
            });

        return () => {
            cancelled = true;
        };
    }, [data.reports]);

    if (!reports.length) {
        return null;
    }

    // The block reaches the chat like any %BEGIN_JSON% block, so the model can write one: a chip opens only this site's
    // own address (the server writes "/extreport/<id>"; security unit 7, HOME 23's address check, isSameSiteHttpUrl).
    const opens = (url: string | undefined): url is string => !!url && isSameSiteHttpUrl(url, roothome);

    const open = (url: string) => {
        if (!opens(url)) {
            return;
        }
        if (isMobile) {
            window.location.href = url;
        } else {
            navigate(url);
        }
    };

    return (
        <Box sx={{ mt: 0.5 }}>
            <Box sx={{ mb: 0.5, color: 'text.secondary' }}>{t('sources')}</Box>
            <Stack gap="4px" data-test="source-reports">
                {reports.map((report) => {
                    const tile = tiles[Number(report.element_id)];
                    // The icon map is keyed on the content type's alias; the tool name is only a fallback
                    // for a tile that predates the alias.
                    const iconName =
                        tile?.content_type_alias || tile?.reporting_tool_name || tile?.external_content_name || '';
                    const chip = (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap="8px"
                            onClick={() => open(report.url)}
                            data-test={
                                report.leg ? `source-leg-${report.leg.leg}` : `source-report-${report.element_id}`
                            }
                            sx={{
                                boxSizing: 'border-box',
                                p: '6px 10px 6px 6px',
                                height: '28px',
                                borderRadius: '5px',
                                border: '1px solid',
                                borderColor: (theme) => alpha(theme.palette.text.primary, 0.16),
                                flex: 'none',
                                cursor: opens(report.url) ? 'pointer' : 'default',
                                '&:hover': { backgroundColor: 'action.hover' },
                            }}
                        >
                            <DatabaseIcon name={iconName} placeholderIconName="report" />
                            <Box
                                component="span"
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    lineHeight: '18px',
                                    color: '#075B7E',
                                    flex: 'none',
                                }}
                            >
                                {report.name}
                            </Box>
                        </Stack>
                    );

                    // A leg chip carries its leg in the standard tooltip (model, measure, window) instead of the tile card.
                    const wrapped = report.leg ? (
                        <HtmlTooltip
                            title={<LegTooltipBody leg={report.leg} t={t} />}
                            placement="left-start"
                            arrow={false}
                            certified={false}
                        >
                            {chip}
                        </HtmlTooltip>
                    ) : tile ? (
                        <TileInfoIcon
                            tile={tile}
                            customFieldsDisplayOnTileInfo={tile.customFieldsDisplayOnTileInfo}
                            anchor="wrapper"
                            placement="left-start"
                        >
                            {chip}
                        </TileInfoIcon>
                    ) : (
                        chip
                    );

                    // The why icon sits beside the chip, outside it, so hovering it never opens the tile card.
                    return (
                        <Stack
                            key={report.leg ? `leg-${report.leg.leg}` : report.element_id}
                            direction="row"
                            alignItems="center"
                            spacing={1}
                        >
                            {wrapped}
                            <ReportWhyTooltip why={report.why} elementId={Number(report.element_id)} />
                        </Stack>
                    );
                })}
            </Stack>
        </Box>
    );
}
